import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Edit2, Trash2, Plus, Volume2, X, RefreshCw, Loader2, AlertTriangle, CheckCircle, Link2, ChevronDown, ChevronRight, Mic, Clock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { getBaseUrl } from '@/services/httpClient';

const CALM_INTERVALS = [
  { value: 30, label: '30 secondes' },
  { value: 60, label: '1 minute' },
  { value: 90, label: '1 min 30' },
  { value: 120, label: '2 minutes' },
  { value: 150, label: '2 min 30' },
  { value: 180, label: '3 minutes' },
];

const AdminVoiceMessages = ({ t }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [category, setCategory] = useState('all');
  const [selectedLang, setSelectedLang] = useState('fr');
  const [selectedVoice, setSelectedVoice] = useState('hera');
  const [messageType, setMessageType] = useState('all');
  const [filterTheme, setFilterTheme] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editFormData, setEditFormData] = useState({ text_fr: '', text_en: '', message_role: null, tags: [], theme: '', trigger_mode: 'session_start', cooldown_minutes: 0, probability: 1.0 });
  const [newMessageText, setNewMessageText] = useState('');
  const [newMessageRole, setNewMessageRole] = useState(null);
  const [newMessageTags, setNewMessageTags] = useState([]);
  const [newMessageTheme, setNewMessageTheme] = useState('generic');
  const [newTriggerMode, setNewTriggerMode] = useState('session_start');
  const [newCooldown, setNewCooldown] = useState(0);
  const [newProbability, setNewProbability] = useState(1.0);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [regenResult, setRegenResult] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [syncNotification, setSyncNotification] = useState(null);
  const [voiceProfileThemes, setVoiceProfileThemes] = useState([]);
  const [voiceProfileOpen, setVoiceProfileOpen] = useState(false);
  const [voiceProfileSaving, setVoiceProfileSaving] = useState(false);
  const [voiceProfileDirty, setVoiceProfileDirty] = useState(false);
  const [calmInterval, setCalmInterval] = useState(60);
  const [calmIntervalSaving, setCalmIntervalSaving] = useState(false);

  // Editorial classification options
  const MESSAGE_ROLES = [
    { value: null, label: 'Regular (default)' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'theme_intro', label: 'Theme Intro' },
    { value: 'presence', label: 'Presence' },
    { value: 'support', label: 'Support' },
    { value: 'closing', label: 'Closing' },
  ];
  const AVAILABLE_TAGS = [
    { group: 'Context', tags: ['onboarding', 'session_start', 'mid_session', 'session_end', 'sos', 'breathing', 'reassurance'] },
    { group: 'Tone', tags: ['calming', 'grounding', 'slowing_down', 'focus', 'release', 'neutral'] },
    { group: 'Usage', tags: ['universal', 'nature', 'relaxation', 'sleep', 'meditation', 'pulse_flow', 'classical', 'chill_lofi'] },
  ];

  // All trigger modes (used in edit modal for backwards compatibility)
  const TRIGGER_MODES = [
    { value: 'session_start', label: 'Welcome (Session Start)', cooldown: 0, probability: 1.0 },
    { value: 'during_session', label: 'During Session (legacy)', cooldown: 20, probability: 0.35 },
    { value: 'transition', label: 'Transition', cooldown: 0, probability: 1.0 },
    { value: 'sos_immediate', label: 'SOS Immediate (legacy)', cooldown: 0, probability: 1.0 },
    { value: 'night_start', label: 'Cocoon (intro)', cooldown: 0, probability: 1.0 },
    { value: 'deep_calm_start', label: 'Deep Calm (intro)', cooldown: 0, probability: 1.0 },
  ];

  // V1.4.0: Only these categories are active for new message creation
  const ACTIVE_TRIGGER_MODES = TRIGGER_MODES.filter(
    tm => tm.value === 'session_start' || tm.value === 'transition' || tm.value === 'night_start' || tm.value === 'deep_calm_start'
  );

  const applyTriggerDefaults = (mode, setCD, setProb) => {
    const def = TRIGGER_MODES.find(t => t.value === mode);
    if (def) { setCD(def.cooldown); setProb(def.probability); }
  };

  useEffect(() => {
    if (user) {
      loadMessages();
    }
  }, [selectedLang, selectedVoice, messageType, category, filterTheme]);

  // Load voice profiles for themes
  const loadVoiceProfiles = useCallback(async () => {
    try {
      const backendUrl = getBaseUrl();
      const response = await fetch(`${backendUrl}/api/admin/themes`);
      if (response.ok) {
        const data = await response.json();
        setVoiceProfileThemes(data.themes || []);
      }
    } catch (error) {
      console.error('Error loading voice profiles:', error);
    }
  }, []);

  useEffect(() => {
    if (user) loadVoiceProfiles();
  }, [user, loadVoiceProfiles]);

  // Load calm interval setting
  useEffect(() => {
    if (category === 'calm') {
      const backendUrl = getBaseUrl();
      fetch(`${backendUrl}/api/admin/settings/calm-interval`)
        .then(r => r.json())
        .then(data => setCalmInterval(data.interval_seconds || 60))
        .catch(() => {});
    }
  }, [category]);

  const saveCalmInterval = async (val) => {
    setCalmIntervalSaving(true);
    try {
      const backendUrl = getBaseUrl();
      await fetch(`${backendUrl}/api/admin/settings/calm-interval`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval_seconds: val }),
      });
      setCalmInterval(val);
    } catch (e) { console.error(e); }
    setCalmIntervalSaving(false);
  };

  const CalmIntervalCard = () => (
    <Card className="mb-6" style={{ backgroundColor: '#1e293b', borderColor: '#334155' }}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base" style={{ color: '#e2e8f0' }}>
          <Clock className="h-4 w-4 text-green-400" />
          Intervalle des messages réguliers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-3" style={{ color: '#94a3b8' }}>
          Les messages réguliers d'apaisement sont diffusés à intervalles réguliers pendant la session SOS Calm.
        </p>
        <div className="flex items-center gap-3">
          <select
            value={calmInterval}
            onChange={(e) => saveCalmInterval(Number(e.target.value))}
            style={{ color: '#e2e8f0', backgroundColor: '#0f172a', borderColor: '#475569' }}
            className="p-2 border rounded-lg font-medium"
            data-testid="calm-interval-select"
          >
            {CALM_INTERVALS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {calmIntervalSaving && <Loader2 className="h-4 w-4 animate-spin text-green-400" />}
          {!calmIntervalSaving && <span className="text-sm" style={{ color: '#64748b' }}>Sauvegardé automatiquement</span>}
        </div>
      </CardContent>
    </Card>
  );

  const saveVoiceProfiles = async () => {
    setVoiceProfileSaving(true);
    try {
      const backendUrl = getBaseUrl();
      const response = await fetch(`${backendUrl}/api/admin/themes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ themes: voiceProfileThemes })
      });
      if (response.ok) {
        setVoiceProfileDirty(false);
        setSyncNotification('Profils vocaux sauvegardés');
        setTimeout(() => setSyncNotification(null), 3000);
      }
    } catch (error) {
      console.error('Error saving voice profiles:', error);
    } finally {
      setVoiceProfileSaving(false);
    }
  };

  const updateThemeOverride = (themeKey, field, value) => {
    setVoiceProfileThemes(prev => prev.map(t => {
      if (t.key !== themeKey) return t;
      const overrides = { ...(t.voice_overrides || {}) };
      if (value === null || value === undefined || value === '') {
        delete overrides[field];
      } else {
        overrides[field] = Number(value);
      }
      return { ...t, voice_overrides: overrides };
    }));
    setVoiceProfileDirty(true);
  };


  const loadMessages = async () => {
    setLoading(true);
    try {
      const backendUrl = getBaseUrl();
      const params = new URLSearchParams({
        lang: selectedLang,
        voice: selectedVoice,
        category,
        message_type: messageType
      });
      if (filterTheme) params.set('theme', filterTheme);
      
      const response = await fetch(`${backendUrl}/api/admin/voice-messages?${params}`);
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('[AdminVoiceMessages] Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const playMessage = async (messageId) => {
    try {
      const backendUrl = getBaseUrl();
      // Use ?stream=true to proxy audio through backend (avoids CSP blocking R2 URLs)
      const audioUrl = `${backendUrl}/api/voice-messages-audio/${messageId}?stream=true`;
      const audio = new Audio(audioUrl);
      audio.play();
    } catch (error) {
      console.error('[AdminVoiceMessages] Error playing message:', error);
    }
  };

  const handleDelete = async (messageId) => {
    setDeletingId(messageId);
    try {
      const backendUrl = getBaseUrl();
      const response = await fetch(`${backendUrl}/api/admin/voice-messages/${messageId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        setConfirmDeleteId(null);
      } else {
        console.error('[AdminVoiceMessages] Delete failed:', response.status);
      }
    } catch (error) {
      console.error('[AdminVoiceMessages] Error deleting message:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRegenerate = async (messageId) => {
    setRegeneratingId(messageId);
    setRegenResult(null);
    try {
      const backendUrl = getBaseUrl();
      const response = await fetch(`${backendUrl}/api/admin/voice-messages/${messageId}/regenerate`, {
        method: 'POST'
      });
      if (response.ok) {
        setRegenResult({ id: messageId, status: 'success', msg: 'Audio régénéré' });
        loadMessages();
      } else {
        const err = await response.json();
        setRegenResult({ id: messageId, status: 'error', msg: err.detail || 'Erreur' });
      }
    } catch (error) {
      setRegenResult({ id: messageId, status: 'error', msg: error.message });
    } finally {
      setRegeneratingId(null);
    }
  };



  const handleEditClick = (message) => {
    setEditingMessage(message);
    setEditFormData({
      text_fr: message.lang === 'fr' ? message.text : '',
      text_en: message.lang === 'en' ? message.text : '',
      message_role: message.message_role || null,
      tags: message.tags || [],
      theme: message.theme || '',
      trigger_mode: message.trigger_mode || 'during_session',
      cooldown_minutes: message.cooldown_minutes ?? 10,
      probability: message.probability ?? 1.0
    });
  };

  const handleEditSave = async () => {
    if (!editingMessage) return;

    try {
      // BUILD 52.5: Use getBaseUrl() for iOS Capacitor compatibility
      const backendUrl = getBaseUrl();
      const textToSave = editingMessage.lang === 'fr' ? editFormData.text_fr : editFormData.text_en;
      
      // Show loading state
      const originalText = editingMessage.text;
      console.log('[AdminVoiceMessages] 📝 handleEditSave:', {
        id: editingMessage.id,
        voice: editingMessage.voice,
        lang: editingMessage.lang,
        originalText: originalText,
        newText: textToSave,
        backendUrl
      });
      
      const requestUrl = `${backendUrl}/api/radio/voice-messages/${editingMessage.id}`;
      const response = await fetch(requestUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: textToSave,
          lang: editingMessage.lang,
          regenerate_audio: false,
          message_role: editFormData.message_role,
          tags: editFormData.tags,
          theme: editFormData.theme,
          trigger_mode: editFormData.trigger_mode,
          cooldown_minutes: editFormData.cooldown_minutes,
          probability: editFormData.probability
        })
      });

      console.log('[AdminVoiceMessages] ✅ handleEditSave response:', { status: response.status });

      if (response.ok) {
        const result = await response.json();
        
        // Show sync notification if a sibling was updated
        if (result.synced_sibling_id) {
          const otherLang = editingMessage.lang === 'fr' ? 'EN' : 'FR';
          setSyncNotification(`Réglages synchronisés vers la version ${otherLang}`);
          setTimeout(() => setSyncNotification(null), 4000);
        }
        
        setEditingMessage(null);
        setEditFormData({ text_fr: '', text_en: '', message_role: null, tags: [], theme: '', trigger_mode: 'session_start', cooldown_minutes: 0, probability: 1.0 });
        loadMessages(); // Reload messages
      } else {
        const errorData = await response.json();
        console.error('[AdminVoiceMessages] ❌ Failed to update message:', errorData);
        console.error(`Erreur: ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('[AdminVoiceMessages] ❌ Error updating message:', error);
      console.error('Erreur lors de la mise à jour du message. Vérifiez votre connexion.');
    }
  };

  const handleEditCancel = () => {
    setEditingMessage(null);
    setEditFormData({ text_fr: '', text_en: '', message_role: null, tags: [], theme: '' });
  };


  const handleToggleActive = async (messageId, newValue) => {
    try {
      const backendUrl = getBaseUrl();
      const res = await fetch(`${backendUrl}/api/admin/voice-messages/${messageId}/toggle-active`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newValue })
      });
      if (res.ok) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_active: newValue } : m));
      }
    } catch (e) {
      console.error('[AdminVoiceMessages] toggle active failed:', e);
    }
  };

  const handleNewMessageClick = () => {
    setNewMessageText('');
    setNewMessageTags([]);
    setNewMessageTheme(filterTheme || 'generic');
    if (category === 'deep_calm') {
      setNewMessageRole(null);
      setNewTriggerMode('deep_calm_start');
      setNewCooldown(0);
      setNewProbability(1.0);
    } else if (messageType === 'welcome') {
      setNewMessageRole('welcome');
      setNewTriggerMode('session_start');
      setNewCooldown(0);
      setNewProbability(1.0);
    } else {
      setNewMessageRole(null);
      setNewTriggerMode('transition');
      setNewCooldown(0);
      setNewProbability(1.0);
    }
    setShowNewMessageModal(true);
  };

  const handleNewMessageSave = async () => {
    console.log('[AdminVoiceMessages] 📝 handleNewMessageSave:', {
      newMessageText,
      length: newMessageText?.length,
      trimmed: newMessageText?.trim()
    });
    
    if (!newMessageText.trim()) {
      return;
    }

    try {
      // BUILD 52.5: Use getBaseUrl() for iOS Capacitor compatibility
      const backendUrl = getBaseUrl();
      
      // For calm category, use 'sos_calm' as channel, otherwise 'ALL'
      const channelToUse = category === 'calm' ? 'sos_calm' : 'ALL';
      // For deep_calm category, default trigger_mode to deep_calm_start
      const triggerToUse = category === 'deep_calm' ? 'deep_calm_start' : newTriggerMode;
      const requestUrl = `${backendUrl}/api/radio/voice-messages`;
      
      console.log('[AdminVoiceMessages] 📡 Creating new message:', {
        backendUrl,
        requestUrl,
        channel: channelToUse,
        voice: selectedVoice,
        lang: selectedLang
      });
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel_id: channelToUse,
          voice: selectedVoice,
          lang: selectedLang,
          text: newMessageText,
          generate_audio: true,
          is_welcome: category === 'radio' && messageType === 'welcome',
          message_role: newMessageRole,
          tags: newMessageTags,
          theme: newMessageTheme || 'generic',
          trigger_mode: triggerToUse,
          cooldown_minutes: newCooldown,
          probability: newProbability
        })
      });

      console.log('[AdminVoiceMessages] ✅ Create response:', { status: response.status });

      if (response.ok) {
        const result = await response.json();
        
        // Show auto-link notification
        if (result.paired_sibling_id) {
          const otherLang = selectedLang === 'fr' ? 'EN' : 'FR';
          setSyncNotification(`Message auto-lié avec la version ${otherLang}`);
          setTimeout(() => setSyncNotification(null), 4000);
        }
        
        setShowNewMessageModal(false);
        setNewMessageText('');
        setNewMessageRole(null);
        setNewMessageTags([]);
        loadMessages(); // Reload messages
      } else {
        const errorData = await response.json();
        console.error('[AdminVoiceMessages] ❌ Failed to create message:', errorData);
        console.error(`Erreur: ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('[AdminVoiceMessages] ❌ Error creating message:', error);
      console.error('Erreur lors de la création du message. Vérifiez votre connexion.');
    }
  };

  const handleNewMessageCancel = () => {
    setShowNewMessageModal(false);
    setNewMessageText('');
    setNewMessageRole(null);
    setNewMessageTags([]);
  };

  if (loading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8 pb-40">
      <div className="max-w-4xl mx-auto">
        <PageHeader t={t} />

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Messages Vocaux - Administration
          </h1>
          <p className="text-gray-600">
            {category === 'calm' 
              ? 'Gerez les messages vocaux d\'apaisement pour SOS Calm'
              : category === 'deep_calm'
              ? 'Gerez les messages vocaux pour Deep Calm'
              : category === 'all'
              ? 'Vue complete de tous les messages vocaux'
              : 'Gerez les messages vocaux pour chaque canal radio'}
          </p>
        </div>

        {/* Category Selector */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Categorie de messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <button
                onClick={() => setCategory('all')}
                className={`p-4 rounded-xl border-2 transition-all min-w-0 text-center ${
                  category === 'all'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
                data-testid="category-all"
              >
                <div className="text-2xl mb-2">&#128218;</div>
                <div className="font-semibold">Tous</div>
                <div className="text-sm text-gray-500">Tous les messages</div>
              </button>
              <button
                onClick={() => setCategory('radio')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  category === 'radio'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                data-testid="category-radio"
              >
                <div className="text-2xl mb-2">&#128251;</div>
                <div className="font-semibold text-sm leading-tight break-words">Messages Presence</div>
                <div className="text-xs text-gray-500 leading-tight break-words mt-1">Canaux Radio Hearen</div>
              </button>
              <button
                onClick={() => setCategory('calm')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  category === 'calm'
                    ? 'border-green-500 bg-green-50 text-green-900'
                    : 'border-gray-200 hover:border-green-300'
                }`}
                data-testid="category-calm"
              >
                <div className="text-2xl mb-2">&#10084;&#65039;</div>
                <div className="font-semibold">Messages d'Apaisement</div>
                <div className="text-sm text-gray-500">SOS Calm</div>
              </button>
              <button
                onClick={() => setCategory('night')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  category === 'night'
                    ? 'border-violet-500 bg-violet-50 text-violet-900'
                    : 'border-gray-200 hover:border-violet-300'
                }`}
                data-testid="category-night"
              >
                <div className="text-2xl mb-2">&#127769;</div>
                <div className="font-semibold">Cocoon</div>
                <div className="text-sm text-gray-500">Message d'intro nocturne</div>
              </button>
              <button
                onClick={() => setCategory('deep_calm')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  category === 'deep_calm'
                    ? 'border-slate-500 bg-slate-50 text-slate-900'
                    : 'border-gray-200 hover:border-slate-300'
                }`}
                data-testid="category-deep-calm"
              >
                <div className="text-2xl mb-2">&#127754;</div>
                <div className="font-semibold">Deep Calm</div>
                <div className="text-sm text-gray-500">Messages Deep Calm</div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Voice Transition Config per Theme — Only for Presence */}
        {category === 'radio' && (
        <Card className="mb-6">
          <CardHeader className="cursor-pointer select-none" onClick={() => setVoiceProfileOpen(!voiceProfileOpen)}>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-purple-600" />
                Fréquence vocale par thème
              </span>
              <span className="flex items-center gap-2">
                {voiceProfileDirty && (
                  <span className="text-xs text-amber-600 font-normal">Non sauvegardé</span>
                )}
                {voiceProfileOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
              </span>
            </CardTitle>
          </CardHeader>
          {voiceProfileOpen && (
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">Message vocal toutes les X pistes (déterministe, pas de probabilité).</p>
              <div className="space-y-1">
                {voiceProfileThemes.filter(t => t.active).map((theme) => {
                  const ov = theme.voice_overrides || {};

                  return (
                    <div key={theme.key} className="rounded-lg overflow-hidden" style={{ backgroundColor: '#1e293b' }} data-testid={`vp-row-${theme.key}`}>
                      <div className="flex items-center justify-between py-2.5 px-3">
                        <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{theme.name_fr}</span>
                        <div className="flex items-center gap-1.5">
                          <label className="text-xs whitespace-nowrap" style={{ color: '#94a3b8' }}>Transition toutes les</label>
                          <select
                            value={ov.transition_every_n_tracks ?? 2}
                            onChange={(e) => updateThemeOverride(theme.key, 'transition_every_n_tracks', e.target.value)}
                            style={{ color: '#e2e8f0', backgroundColor: '#0f172a', borderColor: '#475569' }}
                            className="text-sm rounded-md px-2 py-1.5 border w-16 text-center"
                            data-testid={`vp-transition-n-${theme.key}`}
                          >
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                          </select>
                          <span className="text-xs" style={{ color: '#94a3b8' }}>pistes</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {voiceProfileDirty && (
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={saveVoiceProfiles}
                    disabled={voiceProfileSaving}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="vp-save-btn"
                  >
                    {voiceProfileSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Sauvegarder
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
        )}

        {/* Calm Interval Setting — Only for Apaisement */}
        {category === 'calm' && (
          <CalmIntervalCard />
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtres de messages vocaux</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Message type selector — For all categories */}
            {(category === 'all' || category === 'radio' || category === 'calm') && (
              <div className="mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de message
                  </label>
                  <select
                    value={messageType}
                    onChange={(e) => setMessageType(e.target.value)}
                    className="w-full p-2 border-2 border-purple-300 rounded-lg bg-purple-50 font-medium"
                    data-testid="filter-message-type"
                  >
                    <option value="all">Tous les types</option>
                    <option value="regular">
                      {category === 'calm' ? 'Messages reguliers (intervalle)' : 'Messages reguliers'}
                    </option>
                    <option value="welcome">Messages d'accueil</option>
                  </select>
                </div>
              </div>
            )}

            {/* Language & Voice Selectors - For both Radio and Calm */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Language Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Langue
                </label>
                <select
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="fr">Fran&#231;ais</option>
                  <option value="en">English</option>
                </select>
              </div>

              {/* Voice Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voix
                </label>
                <select
                  value={selectedVoice}
                  onChange={(e) => setSelectedVoice(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="hera">H&#233;ra (Voix f&#233;minine)</option>
                  <option value="auren">Auren (Voix masculine)</option>
                </select>
              </div>

              {/* Theme Filter — For all categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filtrer par th&#232;me
                </label>
                <select
                  value={filterTheme}
                  onChange={(e) => setFilterTheme(e.target.value)}
                  className="w-full p-2 border-2 border-teal-300 rounded-lg bg-teal-50 font-medium"
                  data-testid="filter-theme"
                >
                  <option value="">Tous les th&#232;mes</option>
                  <option value="generic">G&#233;n&#233;rique (tous th&#232;mes)</option>
                  <option value="nature">Nature</option>
                  <option value="relaxation">Relaxation</option>
                  <option value="sleep">Sleep</option>
                  <option value="meditation">Meditation</option>
                  <option value="classical">Classical</option>
                  <option value="pulse_flow">Pulse Flow</option>
                  <option value="chill_lofi">Chill Lo-Fi</option>
                  <option value="sos_calm">SOS Calm</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sync notification */}
        {syncNotification && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm" data-testid="sync-notification">
            <Link2 className="h-4 w-4 shrink-0" />
            {syncNotification}
          </div>
        )}

        {/* Messages List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Messages ({messages.length})</CardTitle>
              <Button
                onClick={handleNewMessageClick}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau message
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  {category === 'calm' 
                    ? (messageType === 'welcome' 
                        ? 'Aucun message d\'accueil d\'apaisement pour cette combinaison'
                        : 'Aucun message régulier d\'apaisement pour cette combinaison')
                    : (messageType === 'welcome' 
                        ? `Aucun message d'accueil pour cette combinaison` 
                        : 'Aucun message régulier pour ce canal')}
                </p>
                {category === 'radio' && messageType === 'welcome' && (
                  <div className="inline-block p-3 bg-blue-50 border border-blue-200 rounded-lg text-left">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Astuce:</strong> Cliquez sur "+ Nouveau message" pour créer le message 
                      d'accueil qui sera joué au démarrage de cette radio.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col gap-3 min-w-0 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                            {message.voice === 'hera' ? 'Héra' : 'Auren'}
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                            {message.lang.toUpperCase()}
                          </span>
                          {message.pair_id && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded" data-testid={`pair-badge-${message.id}`} title="Lié FR ↔ EN : les réglages de diffusion se synchronisent automatiquement">
                              <Link2 className="h-3 w-3" />
                              FR↔EN
                            </span>
                          )}
                        </div>

<div className="min-w-0 flex-1">
  <p className="text-gray-900 break-words">{message.text}</p>

  {message.message_role && (
    <span className="inline-block text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded mt-1 mr-1">
      {message.message_role}
    </span>
  )}

  {message.theme && (
    <span className="inline-block text-xs px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded mt-1 mr-1">
      {message.theme}
    </span>
  )}

  {!message.message_role && !message.theme && !(message.tags && message.tags.length > 0) && (
    <span className="inline-block text-xs px-2 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded mt-1 mr-1 italic">
      Non classifié
    </span>
  )}

  {message.tags && message.tags.length > 0 && (
    <div className="flex flex-wrap gap-1 mt-1">
      {message.tags.map(tag => (
        <span key={tag} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
          {tag}
        </span>
      ))}
    </div>
  )}
</div>
                        {message.message_role && (
                          <span className="inline-block text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded mt-1 mr-1">
                            {message.message_role}
                          </span>
                        )}
                        {message.theme && (
                          <span className="inline-block text-xs px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded mt-1 mr-1">
                            {message.theme}
                          </span>
                        )}
                        {!message.message_role && !message.theme && !(message.tags && message.tags.length > 0) && (
                          <span className="inline-block text-xs px-2 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded mt-1 mr-1 italic">
                            Non classifié
                          </span>
                        )}
                        {message.tags && message.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {message.tags.map(tag => (
                              <span key={tag} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Audio status indicator */}
                        <div className="mt-2">
                          {regeneratingId === message.id ? (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Génération en cours...
                            </span>
                          ) : regenResult?.id === message.id && regenResult.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600">
                              <AlertTriangle className="h-3 w-3" />
                              {regenResult.msg}
                            </span>
                          ) : regenResult?.id === message.id && regenResult.status === 'success' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle className="h-3 w-3" />
                              Audio régénéré
                            </span>
                          ) : message.audio_stale ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600" data-testid={`audio-stale-${message.id}`}>
                              <AlertTriangle className="h-3 w-3" />
                              Texte modifié, audio à régénérer
                            </span>
                          ) : (message.r2_key || message.file_path) ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600" data-testid={`audio-ok-${message.id}`}>
                              <CheckCircle className="h-3 w-3" />
                              Audio à jour
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Pas d'audio</span>
                          )}
                        </div>
                        {/* Trigger mode + params */}
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            message.trigger_mode === 'session_start' ? 'bg-blue-100 text-blue-700' :
                            message.trigger_mode === 'sos_immediate' ? 'bg-red-100 text-red-700' :
                            message.trigger_mode === 'transition' ? 'bg-teal-100 text-teal-700' :
                            message.trigger_mode === 'night_start' ? 'bg-indigo-100 text-indigo-700' :
                            message.trigger_mode === 'deep_calm_start' ? 'bg-slate-100 text-slate-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {message.trigger_mode === 'session_start' ? 'Session Start' :
                             message.trigger_mode === 'sos_immediate' ? 'SOS' :
                             message.trigger_mode === 'transition' ? 'Transition' :
                             message.trigger_mode === 'night_start' ? 'Cocoon' :
                             message.trigger_mode === 'deep_calm_start' ? 'Deep Calm' : 'During Session'}
                          </span>
                          <span className="text-xs text-gray-400">
                            cd:{message.cooldown_minutes ?? '?'}min p:{((message.probability ?? 1) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 shrink-0 flex-wrap sm:ml-2">
                        <div className="flex items-center gap-1.5 mr-2" title={message.is_active === false ? "Inactif" : "Actif"}>
                          <Switch
                            checked={message.is_active !== false}
                            onCheckedChange={(val) => handleToggleActive(message.id, val)}
                            data-testid={`toggle-active-${message.id}`}
                          />
                          <span className={`text-xs ${message.is_active === false ? 'text-gray-400' : 'text-green-600'}`}>
                            {message.is_active === false ? 'Off' : 'On'}
                          </span>
                        </div>
                        <Button
                          type="button"
                          onClick={() => playMessage(message.id)}
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 hover:bg-purple-50 disabled:opacity-30"
                          title={message.r2_key || message.file_path ? "Écouter" : "Pas d'audio"}
                          disabled={!(message.r2_key || message.file_path)}
                          data-testid={`play-${message.id}`}
                        >
                          <Volume2 className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleRegenerate(message.id)}
                          variant="ghost"
                          size="sm"
                          className={message.audio_stale ? "text-amber-600 hover:bg-amber-50" : "text-gray-500 hover:bg-gray-50"}
                          title="Régénérer audio"
                          disabled={regeneratingId === message.id}
                          data-testid={`regen-${message.id}`}
                        >
                          {regeneratingId === message.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <RefreshCw className="h-4 w-4" />}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleEditClick(message)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          title="Modifier"
                          data-testid={`edit-${message.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {confirmDeleteId === message.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              onClick={() => handleDelete(message.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 text-xs font-bold"
                              disabled={deletingId === message.id}
                              data-testid={`confirm-delete-${message.id}`}
                            >
                              {deletingId === message.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Oui'}
                            </Button>
                            <Button
                              type="button"
                              onClick={() => setConfirmDeleteId(null)}
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:bg-gray-50 text-xs"
                            >
                              Non
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            onClick={() => setConfirmDeleteId(message.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-50 hover:text-red-600"
                            title="Supprimer"
                            data-testid={`delete-${message.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>💡 Info:</strong> Les messages sans audio seront automatiquement générés 
            avec TTS (Text-to-Speech) lors de leur première lecture. Voix: Nova pour Héra, Onyx pour Auren.
          </p>
        </div>

        {/* Edit Modal */}
        {editingMessage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleEditCancel}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  Modifier le message
                </h2>
                <button
                  onClick={handleEditCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {editingMessage.voice === 'hera' ? '👩 Héra' : '👨 Auren'}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {editingMessage.lang.toUpperCase()}
                    </span>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                      {editingMessage.theme || 'Sans thème'}
                    </span>
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texte du message ({editingMessage.lang === 'fr' ? 'Français' : 'English'})
                  </label>
                  <textarea
                    value={editingMessage.lang === 'fr' ? editFormData.text_fr : editFormData.text_en}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      [editingMessage.lang === 'fr' ? 'text_fr' : 'text_en']: e.target.value
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="6"
                    placeholder={`Entrez le texte en ${editingMessage.lang === 'fr' ? 'français' : 'anglais'}...`}
                  />
                </div>

                {/* Editorial classification fields */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message role (optional)
                  </label>
                  <select
                    value={editFormData.message_role || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, message_role: e.target.value || null })}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="edit-message-role"
                  >
                    {MESSAGE_ROLES.map(r => (
                      <option key={r.value || 'default'} value={r.value || ''}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Theme selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thème audio (optional)
                  </label>
                  <select
                    value={editFormData.theme || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, theme: e.target.value || '' })}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="edit-theme"
                  >
                    <option value="generic">G&#233;n&#233;rique (tous th&#232;mes)</option>
                    <option value="nature">Nature</option>
                    <option value="relaxation">Relaxation</option>
                    <option value="sleep">Sleep</option>
                    <option value="meditation">Meditation</option>
                    <option value="classical">Classical</option>
                    <option value="pulse_flow">Pulse Flow</option>
                    <option value="chill_lofi">Chill Lo-Fi</option>
                  </select>
                </div>

                {/* Trigger Mode */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Mode
                  </label>
                  <select
                    value={editFormData.trigger_mode || 'during_session'}
                    onChange={(e) => {
                      const mode = e.target.value;
                      const def = TRIGGER_MODES.find(t => t.value === mode);
                      setEditFormData({ ...editFormData, trigger_mode: mode, cooldown_minutes: def?.cooldown ?? editFormData.cooldown_minutes, probability: def?.probability ?? editFormData.probability });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
                    data-testid="edit-trigger-mode"
                  >
                    {TRIGGER_MODES.map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.label}</option>
                    ))}
                  </select>
                  {editFormData.trigger_mode !== 'night_start' && editFormData.trigger_mode !== 'deep_calm_start' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cooldown (min)</label>
                      <input
                        type="number"
                        min="0"
                        value={editFormData.cooldown_minutes ?? 10}
                        onChange={(e) => setEditFormData({ ...editFormData, cooldown_minutes: parseInt(e.target.value) || 0 })}
                        className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
                        data-testid="edit-cooldown"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Probabilit&#233; (0-1)</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={editFormData.probability ?? 1.0}
                        onChange={(e) => setEditFormData({ ...editFormData, probability: parseFloat(e.target.value) || 0 })}
                        className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
                        data-testid="edit-probability"
                      />
                    </div>
                  </div>
                  )}
                </div>


                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (optional)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto" data-testid="edit-tags-selector">
                    {AVAILABLE_TAGS.map(group => (
                      <div key={group.group} className="mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{group.group}</p>
                        <div className="flex flex-wrap gap-1">
                          {group.tags.map(tag => {
                            const selected = (editFormData.tags || []).includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => {
                                  const current = editFormData.tags || [];
                                  setEditFormData({
                                    ...editFormData,
                                    tags: selected ? current.filter(t => t !== tag) : [...current, tag]
                                  });
                                }}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  selected
                                    ? 'bg-purple-100 border-purple-400 text-purple-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {editingMessage.file_path && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      Si vous modifiez le texte, l'audio sera marqué comme obsolète. 
                      Utilisez le bouton <strong>Régénérer audio</strong> après l'enregistrement pour mettre à jour l'audio.
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                <Button
                  onClick={handleEditCancel}
                  variant="outline"
                  className="px-4 py-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleEditSave}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* New Message Modal */}
        {showNewMessageModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleNewMessageCancel}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  {category === 'calm' 
                    ? '❤️ Nouveau message d\'apaisement' 
                    : (messageType === 'welcome' ? '🎙️ Nouveau message d\'accueil' : '💬 Nouveau message régulier')}
                </h2>
                <button
                  onClick={handleNewMessageCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {category === 'calm' && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>❤️ Message d'apaisement:</strong> Ce message sera utilisé dans le module SOS Calm 
                      pour apaiser l'utilisateur en situation de stress ou d'anxiété.
                    </p>
                  </div>
                )}
                {category === 'radio' && messageType === 'welcome' && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>🎙️ Message d'accueil:</strong> Ce message sera joué au démarrage de la radio 
                      pour cette combinaison canal/voix/langue. Il n'y a qu'un seul message d'accueil par combinaison.
                    </p>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      category === 'calm' ? 'bg-green-100 text-green-700' :
                      (messageType === 'welcome' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700')
                    }`}>
                      {category === 'calm' ? '❤️ Apaisement' : (messageType === 'welcome' ? '🎙️ Accueil' : '💬 Régulier')}
                    </span>
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {selectedVoice === 'hera' ? '👩 Héra' : '👨 Auren'}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {selectedLang.toUpperCase()}
                    </span>
                    {category === 'radio' && newMessageTheme && (
                      <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded">
                        {newMessageTheme}
                      </span>
                    )}
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texte du message ({selectedLang === 'fr' ? 'Français' : 'English'})
                  </label>
                  <textarea
                    value={newMessageText}
                    onChange={(e) => setNewMessageText(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="6"
                    placeholder={`Entrez le texte en ${selectedLang === 'fr' ? 'français' : 'anglais'}...`}
                  />
                </div>

                {/* Editorial classification fields */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    R&#244;le du message
                  </label>
                  <select
                    value={newMessageRole || ''}
                    onChange={(e) => setNewMessageRole(e.target.value || null)}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="new-message-role"
                  >
                    {MESSAGE_ROLES.map(r => (
                      <option key={r.value || 'default'} value={r.value || ''}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Theme selector (creation modal) — hidden for night_start */}
                {newTriggerMode !== 'night_start' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Th&#232;me
                  </label>
                  <select
                    value={newMessageTheme}
                    onChange={(e) => setNewMessageTheme(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="new-message-theme"
                  >
                    <option value="generic">G&#233;n&#233;rique (tous th&#232;mes)</option>
                    <option value="nature">Nature</option>
                    <option value="relaxation">Relaxation</option>
                    <option value="sleep">Sleep</option>
                    <option value="meditation">Meditation</option>
                    <option value="classical">Classical</option>
                    <option value="pulse_flow">Pulse Flow</option>
                    <option value="chill_lofi">Chill Lo-Fi</option>
                  </select>
                </div>
                )}

                {/* Trigger Mode (creation modal) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Mode
                  </label>
                  <select
                    value={newTriggerMode}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setNewTriggerMode(mode);
                      applyTriggerDefaults(mode, setNewCooldown, setNewProbability);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent mb-2"
                    data-testid="new-trigger-mode"
                  >
                    {ACTIVE_TRIGGER_MODES.map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.label}</option>
                    ))}
                  </select>
                  {newTriggerMode !== 'night_start' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cooldown (min)</label>
                      <input
                        type="number"
                        min="0"
                        value={newCooldown}
                        onChange={(e) => setNewCooldown(parseInt(e.target.value) || 0)}
                        className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
                        data-testid="new-cooldown"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Probabilit&#233; (0-1)</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={newProbability}
                        onChange={(e) => setNewProbability(parseFloat(e.target.value) || 0)}
                        className="w-full p-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
                        data-testid="new-probability"
                      />
                    </div>
                  </div>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (optional)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto" data-testid="new-tags-selector">
                    {AVAILABLE_TAGS.map(group => (
                      <div key={group.group} className="mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{group.group}</p>
                        <div className="flex flex-wrap gap-1">
                          {group.tags.map(tag => {
                            const selected = newMessageTags.includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => {
                                  setNewMessageTags(selected
                                    ? newMessageTags.filter(t => t !== tag)
                                    : [...newMessageTags, tag]
                                  );
                                }}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  selected
                                    ? 'bg-green-100 border-green-400 text-green-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>🎤 Génération audio:</strong> Un fichier audio sera généré automatiquement 
                    lors de la création du message.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                <Button
                  onClick={handleNewMessageCancel}
                  variant="outline"
                  className="px-4 py-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNewMessageSave();
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                >
                  Créer le message
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVoiceMessages;
